import {Chip} from "@/components/chip/Chip.tsx";
import classNames from "classnames";
import pluralize from "pluralize";
import {useState} from "react";
import {EmptyEventCard} from "../../shared/EmptyEventCard";
import styles from "./ComponentHistoryTable.module.scss";
import type {ComponentHistoryTableEntry} from "./ComponentHistoryTable.types.ts";
import {TableRow} from "./TableRow.tsx";

export interface ComponentHistoryTableProps {
  data?: ComponentHistoryTableEntry[];
  calculationType?: "BY_INCIDENTS" | "BY_CHECKS";
  allowDrillDown?: boolean;
  componentLinkTo?: (serviceId: number) => string;
}

const countLeafComponents = (entries: ComponentHistoryTableEntry[]): number => {
  return entries.reduce((count, entry) => {
    if (entry.isGroup && entry.children) {
      return count + entry.children.length;
    }
    return entry.isGroup ? count : count + 1;
  }, 0);
};

export const ComponentHistoryTable = ({
  data,
  calculationType = "BY_INCIDENTS",
  allowDrillDown = false,
  componentLinkTo,
}: ComponentHistoryTableProps) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(() => new Set());

  const componentData = data ?? [];

  const toggleGroup = (id: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const totalComponents = countLeafComponents(componentData);

  const renderHeaders = () => {
    if (calculationType === "BY_CHECKS") {
      return (
        <>
          <div className={classNames(styles.headerCell, styles.nameCellHeader)}>Name</div>
          <div className={classNames(styles.headerCell, styles.uptimeCell)}>Uptime</div>
          <div className={classNames(styles.headerCell, styles.outagesCell)}>Outages</div>
          <div className={classNames(styles.headerCell, styles.downtimeCell)}>Downtime</div>
          <div className={classNames(styles.headerCell, styles.responseTimeCell)}>
            Avg Response
            <br />
            Time
          </div>
        </>
      );
    }

    // BY_INCIDENTS
    return (
      <>
        <div className={classNames(styles.headerCell, styles.nameCellHeader)}>Name</div>
        <div className={classNames(styles.headerCell, styles.majorOutageCell)}>
          Major
          <br />
          Outage
        </div>
        <div className={classNames(styles.headerCell, styles.partialOutageCell)}>
          Partial
          <br />
          Outage
        </div>
        <div className={classNames(styles.headerCell, styles.degradedCell)}>
          Degraded
          <br />
          Performance
        </div>
        <div className={classNames(styles.headerCell, styles.responseTimeCell)}>
          Avg Response
          <br />
          Time
        </div>
      </>
    );
  };

  return (
    <div className={styles.componentHistoryTable}>
      <div className={styles.sectionHeader}>
        <div className={styles.titleRow}>
          <h4 className={styles.title}>Component History</h4>
          <Chip variant="count" size="tiny" aria-label={pluralize("Component", totalComponents, true)}>
            {totalComponents}
          </Chip>
        </div>
      </div>

      {totalComponents === 0 ? (
        <div className={styles.emptyStateWrapper}>
          <EmptyEventCard message="There is no Component history." />
        </div>
      ) : (
        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>{renderHeaders()}</div>

          <div className={styles.tableBody}>
            {componentData.map((entry, idx) => {
              const isExpanded = expandedGroups.has(entry.id);
              const isLastGroup = idx === componentData.length - 1;
              const children = entry.children ?? [];

              return (
                <div key={entry.id}>
                  <TableRow
                    entry={entry}
                    isExpanded={isExpanded}
                    onToggle={() => toggleGroup(entry.id)}
                    hasDivider
                    calculationType={calculationType}
                    allowDrillDown={allowDrillDown}
                    componentLinkTo={componentLinkTo}
                  />
                  {entry.isGroup && isExpanded && children.length > 0 && (
                    <>
                      {children.map((child) => (
                        <TableRow
                          key={child.id}
                          entry={child}
                          isExpanded={false}
                          onToggle={() => {}}
                          isChild
                          hasDivider
                          calculationType={calculationType}
                          allowDrillDown={allowDrillDown}
                          componentLinkTo={componentLinkTo}
                        />
                      ))}
                      {!isLastGroup && <div className={styles.groupSpacer} />}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
